import { HTMLAttributes } from 'react'

import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Media from '@/components/base/media'
import Motion from '@/components/base/motion'
import ScrollCta from '@/components/base/scroll-cta'
import Typography from '@/components/base/typography'
import { productHeroAnimation } from '@/components/sections/animations'

import { cn } from '@/lib/utils'
import { WPMedia, WPPageData } from '@/types'

export type ProductHeroProps = HTMLAttributes<HTMLElement> & {
  pageProps: WPPageData & {
    acf: {
      hero: {
        title: string
        text: string
        media: WPMedia
      }
    }
  }
}

const ProductHero = ({ pageProps, className, ...props }: ProductHeroProps) => {
  const { title, media, text } = pageProps.acf?.hero

  return (
    <Motion animation={productHeroAnimation}>
      {(motionRef) => (
        <div
          ref={motionRef}
          className={cn(
            'relative mt-[calc(var(--header-height)*-1-43px)] h-auto md:h-[calc(var(--lvh)*200)]',
            className
          )}
          {...props}
        >
          <Container
            className="sticky top-0 h-auto overflow-hidden pt-[calc(var(--header-height)+43px)] md:h-[calc(var(--lvh)*100)]"
            data-motion-container
          >
            <Grid className="md:h-[calc(var(--lvh)*100))] relative flex h-full flex-col justify-between md:grid">
              <div className="relative z-10 col-span-2 md:col-span-3 lg:col-span-3 lg:col-start-2">
                <div
                  className={cn(
                    'flex flex-[1_0_auto] flex-col justify-start gap-spacing-xl pb-[100px] pt-[40px] md:h-full md:pb-spacing-2xl lg:pt-spacing-2xl'
                  )}
                >
                  {title ? (
                    <Typography
                      variant="h1"
                      component="h1"
                      data-motion-title
                      className="ml-0 w-[90vw] [&_.gsap-line]:pr-[.1em]"
                    >
                      {title}
                    </Typography>
                  ) : null}
                  {text ? (
                    <Typography
                      variant="regular"
                      component="p"
                      className="text-text-primary-light dark:text-text-primary-dark"
                      data-motion-text
                    >
                      {text}
                    </Typography>
                  ) : null}

                  <ScrollCta
                    className="absolute bottom-[20px] left-0 z-20 md:bottom-spacing-2xl"
                    data-motion-cta
                  />
                </div>
              </div>
              <div
                className={cn(
                  'relative z-0 col-span-2 flex-[0_0_50%] md:col-span-3 md:mt-[140px] md:!aspect-video md:h-auto lg:col-span-7 lg:col-start-6'
                )}
                style={{
                  aspectRatio:
                    media?.poster?.width && media?.poster?.height
                      ? `${media.poster.width} / ${media.poster.height}`
                      : '16/9'
                }}
              >
                {(media?.poster?.url || media?.video?.url) && (
                  <div
                    data-motion-media
                    className="relative inset-0 z-0 md:absolute md:aspect-video md:h-auto"
                    style={{
                      aspectRatio:
                        media?.poster?.width && media?.poster?.height
                          ? `${media.poster.width} / ${media.poster.height}`
                          : '16/9'
                    }}
                  >
                    <Media
                      media={media}
                      unlockLoaderOnReady
                      priority
                      className={cn('absolute left-0 top-0 h-full w-full')}
                    />
                  </div>
                )}
              </div>
            </Grid>
          </Container>
        </div>
      )}
    </Motion>
  )
}

export default ProductHero
